
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import { technologyIcons, technologyLabels } from "./technologyIcons";

import axios from "axios";


const BRAND_API_URL = (process.env.NODE_ENV === 'development'
    ? "https://ah7cxw1aa6.execute-api.eu-west-1.amazonaws.com/dev"
    : "https://c9rtj2s6a4.execute-api.eu-west-1.amazonaws.com/prod") + "/companies/get-brand";

const OurTechnology = ({ name, tech, panelWarranty }) => {
    // Only show if tech is provided and is an object
    const techKeys = tech ? Object.keys(tech) : [];

    // A technology category can hold a single entry (legacy object shape) or
    // multiple entries (array shape). Normalise to an array either way.
    const normalizeEntries = (value) => {
        if (Array.isArray(value)) return value;
        if (value && typeof value === 'object' && Object.keys(value).length > 0) return [value];
        return [];
    };

    // Flatten every category into a single list of entries (only those with a
    // brand selected), tagging each with its category for ordering/labels.
    const allEntries = [];
    techKeys.forEach((key) => {
        normalizeEntries(tech && tech[key]).forEach((entry, i) => {
            if (entry && entry.brand) {
                allEntries.push({ ...entry, __cat: key, __id: `${key}-${i}` });
            }
        });
    });

    // Display/order preference across categories.
    const CATEGORY_ORDER = ['SolarPanel', 'SolarBattery', 'SolarInverter', 'MonitoringSystem', 'PowerDiverter', 'EVCharger', 'BlackoutProtection', 'SmartDevice', 'Optimiser', 'HeatPump'];
    const sortByOrder = (a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.__cat);
        const bi = CATEGORY_ORDER.indexOf(b.__cat);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    };

    // Group technology by product so each product renders under its own heading.
    const SOLAR_CATEGORIES = ['SolarPanel', 'SolarBattery', 'SolarInverter', 'MonitoringSystem', 'PowerDiverter', 'Optimiser', 'BlackoutProtection', 'SmartDevice'];
    const solarEntries = allEntries.filter((e) => SOLAR_CATEGORIES.includes(e.__cat)).sort(sortByOrder);
    const evEntries = allEntries.filter((e) => e.__cat === 'EVCharger').sort(sortByOrder);
    const heatPumpEntries = allEntries.filter((e) => e.__cat === 'HeatPump' || e.__cat === 'WaterCylinder').sort(sortByOrder);
    const knownCats = new Set([...SOLAR_CATEGORIES, 'EVCharger', 'HeatPump', 'WaterCylinder']);
    const otherEntries = allEntries.filter((e) => !knownCats.has(e.__cat)).sort(sortByOrder);

    // Within the solar section the first solar panel is featured; the rest are cards.
    const solarPanel = solarEntries.find((e) => e.__cat === 'SolarPanel') || null;
    const otherSolar = solarEntries.filter((e) => e !== solarPanel);

    // Use same mobile check as Navigation.js
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;

    // Check if any technology has a brand selected
    const hasBrands = allEntries.length > 0;

    // Brand info state
    const [brandInfo, setBrandInfo] = useState({});
    const [loadingBrands, setLoadingBrands] = useState(false);

    useEffect(() => {
        if (!tech) return;
        const brands = Array.from(new Set(
            Object.values(tech).flatMap(v => normalizeEntries(v)).map(e => e?.brand).filter(Boolean)
        ));
        if (brands.length === 0) return;
        setLoadingBrands(true);
        Promise.all(
            brands.map(brandName =>
                axios.post(BRAND_API_URL, { brandName })
                    .then(res => ({ brandName, data: res.data }))
                    .catch(() => ({ brandName, data: null }))
            )
        ).then(results => {
            const info = {};
            results.forEach(({ brandName, data }) => {
                if (data) info[brandName] = data;
            });
            setBrandInfo(info);
            setLoadingBrands(false);
        });
    }, [tech]);

    // Extra info badge shown next to a card's brand/label.
    const extraInfoFor = (item) => {
        if (item.__cat === 'EVCharger' && item.model) return item.model;
        if (item.__cat === 'SolarBattery' && item.capacity) return `${item.capacity} kWh`;
        if (item.__cat === 'SolarInverter' && item.powerRating) return `${item.powerRating} kW`;
        if (item.__cat === 'HeatPump' && item.outputKw) return `${item.outputKw} kW`;
        if (item.__cat === 'WaterCylinder' && item.capacity) return `${item.capacity} L`;
        return null;
    };

    // Reliability / Origin / Warranty info row, matching the solar installation
    // layout. Shown on heat pump and EV charger cards.
    const renderInfoRow = (item) => {
        const origin = brandInfo[item.brand]?.attributes?.[item.__cat]?.origin;
        const warranty = item.warranty;
        return (
            <Grid container columnSpacing={6} rowSpacing={2} style={{ fontSize: 16, color: "#444", marginTop: 16 }}>
                <Grid item style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 600 }}>Reliability</span>
                    <span>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <span key={i} style={{ color: '#FFD700', fontSize: 20, marginRight: 2 }}>★</span>
                        ))}
                    </span>
                </Grid>
                <Grid item style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 600 }}>Origin</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {(() => {
                            var countries = require("i18n-iso-countries");
                            countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
                            if (!origin) return 'Unknown';
                            let countryName = countries.getName(origin, 'en');
                            // fallback: try uppercase
                            if (!countryName && origin.length === 2) {
                                countryName = countries.getName(origin.toUpperCase(), 'en');
                            }
                            // fallback: try lowercase
                            if (!countryName && origin.length === 2) {
                                countryName = countries.getName(origin.toLowerCase(), 'en');
                            }
                            // Exception: Show "China" instead of "People's Republic of China"
                            if (countryName === "People's Republic of China") {
                                countryName = "China";
                            }
                            return (
                                <>
                                    <img
                                        src={`https://flagcdn.com/w20/${origin.toLowerCase()}.png`}
                                        alt={countryName || origin}
                                        style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2, marginRight: 4, verticalAlign: 'top', alignSelf: 'flex-start', marginTop: 7 }}
                                    />
                                    <span style={{ maxWidth: 100, whiteSpace: 'normal', wordBreak: 'break-word', display: 'inline-block' }}>{countryName || origin}</span>
                                </>
                            );
                        })()}
                    </span>
                </Grid>
                <Grid item style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 600 }}>Warranty</span>
                    <span>{warranty ? warranty : ''}</span>
                </Grid>
            </Grid>
        );
    };

    // Shared small card for a single technology entry.
    const renderSmallCard = (item) => {
        const { brand, image: customImage, description } = item;
        const extraInfo = extraInfoFor(item);
        const brandLogo = brandInfo[brand]?.logo;
        const displayImage = customImage || brandLogo;
        // Extra heat pump badges shown alongside the output badge.
        const secondaryBadges = item.__cat === 'HeatPump'
            ? [item.source].filter(Boolean)
            : [];
        // Show the reliability/origin/warranty info row on heat pump and EV charger cards.
        const showInfoRow = item.__cat === 'HeatPump' || item.__cat === 'EVCharger';
        return (
            <Grid key={item.__id} container direction="column" style={{
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: 0,
                padding: 0,
                width: '100%',
                overflow: 'hidden',
                marginTop: 16,
            }}>
                <Grid container alignItems="flex-start" wrap="nowrap" style={{ padding: 20, flexWrap: 'nowrap' }}>
                    <Grid item style={{ marginRight: 20, flexShrink: 0, display: 'flex', alignItems: 'flex-start' }}>
                        {loadingBrands ? (
                            <Skeleton variant="circular" width={75} height={75} />
                        ) : displayImage ? (
                            <img
                                src={displayImage}
                                alt={brand + " logo"}
                                style={{ width: 75, height: 75, objectFit: 'contain', background: '#fff', display: 'block' }}
                            />
                        ) : null}
                    </Grid>
                    <Grid item xs style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                            <span style={{ fontWeight: 700, fontSize: 18, color: '#229626' }}>{brand || 'Brand Name'}</span>
                            <span style={{ fontWeight: 700, fontSize: 18, color: '#229626' }}>{technologyLabels[item.__cat] || 'Product Type'}</span>
                            {extraInfo && (
                                <span style={{ fontSize: 14, color: '#666', fontWeight: 500, backgroundColor: '#f5f5f5', padding: '2px 10px', borderRadius: 4 }}>{extraInfo}</span>
                            )}
                            {secondaryBadges.map((badge) => (
                                <span key={badge} style={{ fontSize: 14, color: '#666', fontWeight: 500, backgroundColor: '#f5f5f5', padding: '2px 10px', borderRadius: 4 }}>{badge}</span>
                            ))}
                        </div>
                        {description && (
                            <div style={{ fontSize: 13, color: '#888', marginTop: 6, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.5' }}>{description}</div>
                        )}
                        {showInfoRow && renderInfoRow(item)}
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    if (!hasBrands) return null;

    return (
        <Grid
            container
            style={{
                padding: isMobile ? '60px 30px' : '60px 60px',
                backgroundColor: "#fff",
                borderRadius: "11px"
            }}
        >
            {/* Solar section heading */}
            {solarEntries.length > 0 && (
                <Grid item size={12}>
                    <h3 style={{ margin: 0, marginBottom: 20, fontSize: '26px' }}>Our Standard Solar Installation</h3>
                </Grid>
            )}
            {/* Solar entries with no featured panel: simple card grid */}
            {solarEntries.length > 0 && !solarPanel && (
                <Grid container spacing={2} style={{ marginBottom: 40 }}>
                    {otherSolar.map((item) => (
                        <Grid item size={12} key={item.__id}>
                            {renderSmallCard(item)}
                        </Grid>
                    ))}
                </Grid>
            )}
            {/* Solar Panel Featured Section + 5 small cards to the right */}
            {solarPanel && (
                <Grid container spacing={4} style={{ marginBottom: 40 }}>
                    {/* Main Solar Panel Card */}
                    <Grid item size={{ xs: 12, md: 6 }} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Grid container direction="column" wrap="nowrap" style={{
                            background: "#fff",
                            border: "1px solid #ccc",
                            borderRadius: 0,
                            minHeight: 220,
                            overflow: "hidden",
                            padding: 0,
                            margin: 0,
                            flexWrap: "nowrap",
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Image with brand title overlay - always use stock image for background */}
                            <Grid item style={{ width: "100%", flex: 1, minHeight: 220, position: "relative", padding: 0, margin: 0, display: 'flex' }}>
                                <img
                                    src={require("./images/LONGi-Hi-MO-X6-Solar-Panels.jpg")}
                                    alt="Solar Panel"
                                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", position: 'absolute', left: 0, top: 0 }}
                                />
                                <Grid container alignItems="center" justifyContent="center" style={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    width: "100%",
                                    height: "100%",
                                    background: "rgba(0,0,0,0.32)",
                                    pointerEvents: "none"
                                }}>
                                    <Grid item>
                                        <span style={{
                                            color: "#fff",
                                            fontSize: 36,
                                            fontWeight: 800,
                                            textShadow: "0 2px 12px rgba(0,0,0,0.45)",
                                            letterSpacing: 1,
                                            padding: "8px 32px",
                                            display: 'block',
                                            textAlign: 'center'
                                        }}>
                                            {solarPanel.brand || "Brand Name"}
                                            <br />
                                            <span style={{
                                                fontSize: 20,
                                                fontWeight: 400,
                                                color: '#e0e0e0',
                                                letterSpacing: 0.5,
                                                display: 'block',
                                                marginTop: 4
                                            }}>
                                                {solarPanel.wattage ? `${solarPanel.wattage}W Panels` : ''}
                                            </span>
                                        </span>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {/* Bottom section: logo left, text right */}
                            <Grid container direction="column" style={{ padding: 36, paddingTop: 28 }}>
                                {/* Row: logo left, text right (desktop), logo above blurb (mobile) */}
                                <Grid
                                    container
                                    direction={isMobile ? "column" : "row"}
                                    alignItems={isMobile ? "center" : "flex-start"}
                                    justifyContent="space-between"
                                    wrap={isMobile ? "wrap" : "nowrap"}
                                    style={{ marginBottom: 16 }}
                                >
                                    {/* Logo on top for mobile, left for desktop */}
                                    {isMobile && (
                                        <Grid item style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, minWidth: 120, minHeight: 120 }}>
                                            {(() => {
                                                const brand = solarPanel?.brand;
                                                const customImage = solarPanel?.image;
                                                const brandLogo = brandInfo[brand]?.logo;
                                                const displayImage = customImage || brandLogo;
                                                if (loadingBrands && !customImage) {
                                                    return <Skeleton variant="rectangular" width={120} height={120} />;
                                                }
                                                if (displayImage) {
                                                    return (
                                                        <img
                                                            src={displayImage}
                                                            alt={brand + " logo"}
                                                            style={{ width: 120, height: 120, objectFit: "contain", background: "#fff", margin: 0 }}
                                                        />
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </Grid>
                                    )}
                                    {!isMobile && (
                                        <Grid item style={{ display: "flex", alignItems: "flex-start", marginRight: 24, minWidth: 120, minHeight: 120 }}>
                                            {(() => {
                                                const brand = solarPanel?.brand;
                                                const customImage = solarPanel?.image;
                                                const brandLogo = brandInfo[brand]?.logo;
                                                const displayImage = customImage || brandLogo;
                                                if (loadingBrands && !customImage) {
                                                    return <Skeleton variant="rectangular" width={120} height={120} />;
                                                }
                                                if (displayImage) {
                                                    return (
                                                        <img
                                                            src={displayImage}
                                                            alt={brand + " logo"}
                                                            style={{ width: 120, height: 120, objectFit: "contain", background: "#fff", margin: 0 }}
                                                        />
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </Grid>
                                    )}
                                    <Grid item xs>
                                        <div
                                            style={{
                                                fontSize: 18,
                                                color: "#333",
                                                lineHeight: '27.2px',
                                                textAlign: isMobile ? "center" : "left",
                                                marginBottom: isMobile ? 24 : 0
                                            }}
                                        >
                                            {(() => {
                                                const brand = solarPanel?.brand;
                                                const providerDescription = solarPanel?.description;
                                                const blurb = brandInfo[brand]?.attributes?.SolarPanel?.blurb;
                                                return providerDescription || blurb || "Leading solar panel technology for maximum efficiency and durability.";
                                            })()}
                                        </div>
                                    </Grid>
                                </Grid>
                                {/* Row: reliability, warranty and origin */}
                                <Grid container columnSpacing={6} rowSpacing={2} style={{ fontSize: 16, color: "#444" }}>
                                    <Grid item style={{ display: "flex", flexDirection: "column" }}>
                                        <span style={{ fontWeight: 600 }}>Reliability</span>
                                        <span>
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <span key={i} style={{ color: '#FFD700', fontSize: 20, marginRight: 2 }}>★</span>
                                            ))}
                                        </span>
                                    </Grid>
                                    <Grid item style={{ display: "flex", flexDirection: "column" }}>
                                        <span style={{ fontWeight: 600 }}>Origin</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {(() => {
                                                const brand = solarPanel?.brand;
                                                const origin = brandInfo[brand]?.attributes?.SolarPanel?.origin;
                                                var countries = require("i18n-iso-countries");
                                                countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
                                                if (!origin) return 'Unknown';
                                                let countryName = countries.getName(origin, 'en');
                                                // fallback: try uppercase
                                                if (!countryName && origin.length === 2) {
                                                    countryName = countries.getName(origin.toUpperCase(), 'en');
                                                }
                                                // fallback: try lowercase
                                                if (!countryName && origin.length === 2) {
                                                    countryName = countries.getName(origin.toLowerCase(), 'en');
                                                }
                                                // Exception: Show "China" instead of "People's Republic of China"
                                                if (countryName === "People's Republic of China") {
                                                    countryName = "China";
                                                }
                                                return (
                                                    <>
                                                        <img
                                                            src={`https://flagcdn.com/w20/${origin.toLowerCase()}.png`}
                                                            alt={countryName || origin}
                                                            style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2, marginRight: 4, verticalAlign: 'top', alignSelf: 'flex-start', marginTop: 7 }}
                                                        />
                                                        <span style={{ maxWidth: 100, whiteSpace: 'normal', wordBreak: 'break-word', display: 'inline-block' }}>{countryName || origin}</span>
                                                    </>
                                                );
                                            })()}
                                        </span>
                                    </Grid>
                                    <Grid item style={{ display: "flex", flexDirection: "column" }}>
                                        <span style={{ fontWeight: 600 }}>Warranty</span>
                                        <span>{panelWarranty ? panelWarranty : ''}</span>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    {/* Overflow small cards in the left column under the solar panel card */}
                    {(() => {
                        const overflow = otherSolar.length > 5 ? otherSolar.slice(0, otherSolar.length - 5) : [];
                        return overflow.map(renderSmallCard);
                    })()}
                    </Grid>
                    {/* Small Cards Stacked Vertically, right of main card (last 5 in sort order) */}
                    <Grid item size={{ xs: 12, md: 6 }} style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'flex-start' }}>
                        {otherSolar.slice(-5).map(renderSmallCard)}
                    </Grid>
                </Grid>
            )}
            {/* Heat Pumps section */}
            {heatPumpEntries.length > 0 && (
                <>
                    <Grid item size={12}>
                        <h3 style={{ margin: 0, marginBottom: 20, fontSize: '26px' }}>Heat Pumps</h3>
                    </Grid>
                    <Grid container spacing={2} style={{ marginBottom: 40 }}>
                        {heatPumpEntries.map((item) => (
                            <Grid item size={12} key={item.__id}>
                                {renderSmallCard(item)}
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
            {/* EV Chargers section */}
            {evEntries.length > 0 && (
                <>
                    <Grid item size={12}>
                        <h3 style={{ margin: 0, marginBottom: 20, fontSize: '26px' }}>EV Chargers</h3>
                    </Grid>
                    <Grid container spacing={2} style={{ marginBottom: 40 }}>
                        {evEntries.map((item) => (
                            <Grid item size={12} key={item.__id}>
                                {renderSmallCard(item)}
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
            {/* Any other technology */}
            {otherEntries.length > 0 && (
                <>
                    <Grid item size={12}>
                        <h3 style={{ margin: 0, marginBottom: 20, fontSize: '26px' }}>Our Technology</h3>
                    </Grid>
                    <Grid container spacing={2} style={{ marginBottom: 40 }}>
                        {otherEntries.map((item) => (
                            <Grid item size={12} key={item.__id}>
                                {renderSmallCard(item)}
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default OurTechnology;
